import { memo } from 'react';

import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';
import { round } from '@/utils/mathUtils';

// Pre-define styles to avoid recreating on every render
const PRICE_STYLES = {
  container: 'h-16 w-full flex items-center justify-center',
  priceBox:
    'flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium text-lg',
  different:
    'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50',
  normal:
    'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-800/50',
  icon: 'h-4 w-4 text-current opacity-70',
};

type Props = {
  item: IOneItemInPrepareForRequest;
};

function PriceCol({ item }: Props) {
  // Format and calculate price values
  const price = round(item?.Price || 0, 2);
  const healthyPrice = round(item?.healthyItemPrice || 0, 2);
  const isPriceDiff = price !== healthyPrice;
  const formattedPrice = formatNumberWithCommas(price, 2);

  // Calculate percentage difference if healthy price exists
  const percentDiff =
    healthyPrice > 0
      ? Math.round(((price - healthyPrice) / healthyPrice) * 100)
      : 0;

  return (
    <div className={PRICE_STYLES.container}>
      <div
        className={cn(
          PRICE_STYLES.priceBox,
          isPriceDiff ? PRICE_STYLES.different : PRICE_STYLES.normal,
          'h-full w-full flex-col items-center justify-center',
        )}
      >
        <span>{formattedPrice}</span>

        {/* Show percentage difference if prices differ */}
        {isPriceDiff && healthyPrice > 0 && (
          <span
            className={cn(
              'ml-1 rounded px-1.5 py-0.5 text-xs font-bold',
              percentDiff > 0
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
            )}
          >
            {percentDiff > 0 ? '+' : ''}
            {percentDiff}%
          </span>
        )}
      </div>
    </div>
  );
}

// Use memoization to prevent unnecessary re-renders
export default memo(PriceCol, (prevProps, nextProps) => {
  return (
    round(prevProps.item?.Price || 0, 2) ===
      round(nextProps.item?.Price || 0, 2) &&
    round(prevProps.item?.healthyItemPrice || 0, 2) ===
      round(nextProps.item?.healthyItemPrice || 0, 2)
  );
});
